import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import FujiModal from '../components/fuji-modal/FujiModal';
import ApplePayRefundConfirmModal from '../components/ApplePayRefundConfirmModal/ApplePayRefundConfirmModal';
import WarningModal from '../components/WarningModal/WarningModal';
import {
    isChildTicket,
    isComboTicket,
    isReservationCanceled,
    isReserveTicket,
    showNotificationDialog,
    isQrCodeScanned,
    isRefunded,
    isRefundPending,
} from '../utils';
import { ORDER_STATUS, TIMEOUT_DURATION, USAGE_TICKET_STATUS } from '../utils/Constants';
import useR05Api from '../api/useR05Api';
import { ErrorType, OrderTicket } from 'types';
import { isReactNativeWebView, sendMessageToNativeApp } from '../utils/reactNativeUtils';

const useRefundTicket = () => {
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isApplePayRefundConfirmModalOpen, setIsApplePayRefundConfirmModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string>('');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { fetchOrderDetail, refundTicket: refundTicketApi, refundApplePayVisa } = useR05Api();

    const showErrorNotification = useCallback(
        (errorText: string) => {
            showNotificationDialog('error', '', t('notification.title'), errorText);
        },
        [t],
    );

    const confirmModalOpen = useCallback(() => {
        setIsConfirmModalOpen(true);
    }, []);

    const confirmModalClose = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    /**
     * エラーが発生したら、Notificationにてユーザーに通知する。
     * @param {Error} error
     */
    const handleShowErrorMessage = useCallback(
        (error: ErrorType) => {
            switch (error.errorType) {
                case 'apple_pay_visa_cannot_change_amount':
                    if (window.ApplePaySession) {
                        setIsApplePayRefundConfirmModalOpen(true);
                    } else {
                        showErrorNotification(t('error-message.refund.apple-pay-refund-not-support'));
                    }
                    break;

                case 'already_refunded': // すでに払い戻しされている
                    showErrorNotification(t('error-message.refund.already-refunded'));
                    break;
                case 'out_of_date': // 払戻可能期限を過ぎているため払戻出来ない
                    showErrorNotification(t('error-message.refund.out-of-date'));
                    break;
                case 'cannot_refund_used_order': //チケットが使用済み
                case 'cannot_refund_order_activated': //チケットが有効化済み
                    showErrorNotification(t('error-message.refund.already-activated'));
                    break;
                case 'not_purchased_order': // ユーザーが購入したチケットではない
                case 'cannot_refund_order': // 払い戻し金額が0円以下
                case 'invalid_status': // 払い戻し可能なチケットではない
                case 'no_order': // チケットが見つからない
                case 'not_found_sale_ticket': //該当する個別チケット情報が見つかりません
                    showErrorNotification(t('error-message.refund.forbidden'));
                    break;
                case 'undefined_parameter':
                case 'invalid_parameter':
                case 'out_of_date_token':
                case 'invalid_signature_token':
                case 'invalid_api_key':
                case 'gmo_error':
                    showErrorNotification(t('error-message.refund.failed'));
                    break;
                case 'refund_fee_refunded':
                case 'refund_fee_refund_failed':
                case 'applepay_visa_refund_failed':
                    showErrorNotification(t('error-message.refund.apple-pay-failed'));
                    break;
                case 'card_declined':
                    setWarningMessage(t('error-message.refund.card-declined') ?? '');
                    setIsWarningModalOpen(true);
                    break;
                case 'unexpected': //5xxエラー
                default:
                    showErrorNotification(t('error-message.activation.unexpected-error'));
                    break;
            }
        },
        [showErrorNotification, t],
    );

    const canRefundTicket = useCallback(
        (orderTicket?: OrderTicket) => {
            if (!orderTicket) {
                return {
                    canRefund: false,
                    showNotification() {},
                };
            }

            //払い戻し済みと予約券場合は予約状態チェック。
            if (isRefunded(orderTicket) || isRefundPending(orderTicket) || isReservationCanceled(orderTicket)) {
                return {
                    canRefund: false,
                    showNotification() {
                        showErrorNotification(t('error-message.refund.already-refunded'));
                    },
                };
            }

            const { orderStatus, refundDeadlineAt, refundAmount, remainingOrderDistribution, orderDistributionStatus } =
                orderTicket;
            //無条件払戻が許可されている場合は払い戻しできるようにする
            if (
                orderStatus === ORDER_STATUS.USER_REFUND_ENABLED ||
                orderStatus === ORDER_STATUS.STOCK_USER_REFUND_ENABLED
            ) {
                return {
                    canRefund: true,
                    showNotification() {},
                };
            }

            const now = dayjs();

            //払戻期限を過ぎていないことチェック
            if (now.isAfter(refundDeadlineAt)) {
                return {
                    canRefund: false,
                    showNotification() {
                        showErrorNotification(t('error-message.refund.out-of-date'));
                    },
                };
            }

            //払戻金額が1円以上であるこチェック
            if (isChildTicket(orderTicket) || (refundAmount != null && refundAmount < 1)) {
                return {
                    canRefund: false,
                    showNotification() {
                        showErrorNotification(t('error-message.refund.forbidden'));
                    },
                };
            }

            const remainingOrderDistributionStatus = remainingOrderDistribution?.remainingOrderDistributionStatus;

            // QRチケットが使用済みである場合
            // QRチケットはorderUsageTickets.statusがactivatedにならない
            if (isQrCodeScanned(remainingOrderDistributionStatus, orderDistributionStatus)) {
                return {
                    canRefund: false,
                    showNotification() {
                        showErrorNotification(t('error-message.refund.already-activated'));
                    },
                };
            }
            // 未使用のチケットであること
            const isUnused = orderTicket.comboItems?.every((item) =>
                item?.orderUsageTickets?.every((ticket) => ticket.status === USAGE_TICKET_STATUS.BOUGHT),
            );

            if (!isUnused) {
                return {
                    canRefund: false,
                    showNotification() {
                        showErrorNotification(t('error-message.refund.already-activated'));
                    },
                };
            }

            return {
                canRefund: true,
                showNotification() {},
            };
        },
        [showErrorNotification, t],
    );

    /**
     * チケットが払戻すできるかチェックする。
     * @param {Object} ticket
     */
    const checkRefundTicket = useCallback(
        async (orderTicket?: OrderTicket) => {
            if (!orderTicket) {
                showErrorNotification(t('error-message.refund.forbidden'));
                return {
                    isPassed: false,
                    data: null,
                };
            }
            if (navigator.onLine) {
                let timeoutId;
                let isTimeout = false;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        isTimeout = true;
                        showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                    }, TIMEOUT_DURATION);
                    setLoading(true);
                    let data = await fetchOrderDetail({
                        orderId: orderTicket.orderId,
                        orderTicketId: orderTicket.orderTicketId,
                    });
                    if (!isComboTicket(data)) {
                        data = { ...data, ...data.comboItems[0] };
                    }
                    clearTimeout(timeoutId);
                    setLoading(false);
                    if (canRefundTicket(data).canRefund && !isTimeout) {
                        return {
                            isPassed: true,
                            data: data,
                        };
                    } else {
                        canRefundTicket(data).showNotification();
                        return {
                            isPassed: false,
                            data: null,
                        };
                    }
                } catch {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                    return {
                        isPassed: false,
                        data: null,
                    };
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
                return {
                    isPassed: false,
                    data: null,
                };
            }
        },
        [canRefundTicket, fetchOrderDetail, showErrorNotification, t],
    );

    /**
     * 払戻し処理
     * チェック・タイムアウト・エラーダイアログ表示・遷移まで
     * @param {Object} orderTicket
     */
    const refundTicket = useCallback(
        async (orderTicket: OrderTicket) => {
            setIsConfirmModalOpen(false);

            if (navigator.onLine) {
                const { isPassed, data } = await checkRefundTicket(orderTicket);
                if (!isPassed || !data) {
                    return;
                }
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.refund.timeout'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    const { message } = await refundTicketApi({
                        orderId: data.orderId,
                        cancellationFee: data.cancellationFee,
                    });
                    clearTimeout(timeoutId);
                    setLoading(false);
                    if (message?.includes('already_refunded')) {
                        showErrorNotification(t('error-message.refund.already-refunded'));
                    } else {
                        // 払戻完了
                        showNotificationDialog(
                            'success',
                            '',
                            t('notification.title'),
                            t('notification.refund-succeed'),
                        );
                        if (isReactNativeWebView()) {
                            // WebView内の場合はネイティブアプリに戻る指示を送る
                            sendMessageToNativeApp('go_back', { from: 'refund_ticket' });
                            return;
                        }
                        navigate('/bought', {
                            state: { orderId: data.orderId, orderTicketId: data.orderTicketId },
                        });
                    }
                } catch (error) {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    handleShowErrorMessage(error as ErrorType);
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [checkRefundTicket, handleShowErrorMessage, navigate, refundTicketApi, showErrorNotification, t],
    );

    /**
     *  ApplePay払戻し処理
     * チェック・タイムアウト・エラーダイアログ表示・遷移まで
     * @param {Object} orderTicket
     */
    const refundTicketWithApplePay = useCallback(
        async (
            orderTicket: OrderTicket,
            params: {
                orderId: string;
                cardToken?: string;
            },
        ) => {
            setIsConfirmModalOpen(false);

            if (navigator.onLine) {
                const { isPassed, data } = await checkRefundTicket(orderTicket);
                if (!isPassed || !data) {
                    return;
                }
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.refund.timeout'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    const { message } = await refundApplePayVisa(params);
                    clearTimeout(timeoutId);
                    setLoading(false);
                    if (message?.includes('already_refunded')) {
                        showErrorNotification(t('error-message.refund.already-refunded'));
                        return false;
                    } else {
                        const cancellationFee = data?.cancellationFee ?? 0;
                        showNotificationDialog(
                            'success',
                            '',
                            t('notification.title'),
                            t('notification.apple-pay-refund-succeed', {
                                totalPrice: data.amount,
                                refundFee: (data.refundFee ?? 0) + cancellationFee,
                            }),
                        );
                        navigate('/bought', {
                            state: { orderId: data.orderId, orderTicketId: data.orderTicketId },
                        });
                        return true;
                    }
                } catch (error) {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    handleShowErrorMessage(error as ErrorType);
                    return false;
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [checkRefundTicket, handleShowErrorMessage, navigate, refundApplePayVisa, showErrorNotification, t],
    );

    /**
     * 確認モーダル
     */
    const RefundConfirmModal = useCallback(
        ({ orderTicket }: { orderTicket: OrderTicket }) => {
            return (
                <FujiModal
                    isOpen={isConfirmModalOpen}
                    onCancel={confirmModalClose}
                    txtCancel={t('modal.refund-cancel') ?? ''}
                    onSubmit={() => void refundTicket(orderTicket)}
                    txtSubmit={t('modal.refund-ok')}
                    body={`${t('modal.refund-confirmation')}\n${
                        isReserveTicket(orderTicket) ? t('modal.refund-reservation-ticket') : ''
                    }`}
                />
            );
        },
        [isConfirmModalOpen, t, refundTicket, confirmModalClose],
    );

    const ApplePayConfirmModal = useCallback(
        ({ orderTicket }: { orderTicket: OrderTicket }) => {
            return (
                <>
                    {isApplePayRefundConfirmModalOpen && (
                        <ApplePayRefundConfirmModal
                            orderTicket={orderTicket}
                            isConfirmModalOpen={isApplePayRefundConfirmModalOpen}
                            setIsConfirmModalOpen={setIsApplePayRefundConfirmModalOpen}
                            refundTicketWithApplePay={refundTicketWithApplePay}
                        />
                    )}
                </>
            );
        },
        [isApplePayRefundConfirmModalOpen, refundTicketWithApplePay],
    );

    const WarningConfirmModal = useCallback<() => JSX.Element>(() => {
        return (
            <WarningModal
                isOpen={isWarningModalOpen}
                onSubmit={() => {
                    setIsWarningModalOpen(false);
                }}
                txtSubmit={t('modal.warning-confirm')}
                body={warningMessage}
            />
        );
    }, [isWarningModalOpen, t, warningMessage]);
    return {
        RefundConfirmModal,
        ApplePayConfirmModal,
        WarningConfirmModal,
        checkRefundTicket,
        confirmModalOpen,
        canRefundTicket,
        loading,
    };
};

export default useRefundTicket;
